<template>
  <div class="wework-page">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/" class="wework-breadcrumb">
      <el-breadcrumb-item :to="{ path: '/home' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>运维管理</el-breadcrumb-item>
      <el-breadcrumb-item>数据监控</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 主内容区 -->
    <el-card class="wework-card" shadow="hover">
      <!-- 工具栏 -->
      <div class="tab-toolbar">
        <span class="toolbar-label">选择月份：</span>
        <el-date-picker
          v-model="currentMonth"
          type="month"
          placeholder="选择月份"
          size="small"
          style="width: 160px"
          value-format="yyyy-MM"
          :clearable="false"
          @change="loadAll"
        ></el-date-picker>
        <el-button size="small" style="margin-left: 12px" @click="changeMonth(-1)">上月</el-button>
        <el-button size="small" @click="changeMonth(1)">下月</el-button>
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="loadAll" style="margin-left: 8px">全部刷新</el-button>
      </div>

      <!-- 日历网格 -->
      <div class="calendar-grid" v-loading="anyLoading">
        <div v-for="item in sourceList" :key="item.key" class="calendar-item">
          <!-- 日历标题 -->
          <div class="calendar-title">
            <span>{{ item.label }}</span>
            <el-button
              type="text"
              size="mini"
              icon="el-icon-refresh"
              :loading="calendarData[item.key].loading"
              @click="refreshCalendar(item.key)"
            ></el-button>
          </div>

          <!-- 日历主体 -->
          <el-calendar v-model="calendarDate" class="monitor-calendar">
            <template slot="dateCell" slot-scope="{ data }">
              <div
                v-if="data.type === 'current-month'"
                :class="['calendar-day', { 'has-data': getCount(item.key, data) > 0, 'no-data': getCount(item.key, data) === 0 }]"
                :title="getCount(item.key, data) === 0 ? '点击前往采集' : ''"
                @click="handleDayClick(item.key, data)"
              >
                <span class="day-number">{{ getDayNumber(data.day) }}</span>
                <span v-if="getCount(item.key, data) > 0" class="cell-count">
                  {{ getCount(item.key, data) }}
                </span>
                <span v-else class="cell-hint">去采集</span>
              </div>
              <div v-else class="calendar-day other-month">
                <span class="day-number">{{ getDayNumber(data.day) }}</span>
              </div>
            </template>
          </el-calendar>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
// 数据源到采集平台的映射（用于跳转到采集页）
const SOURCE_PLATFORM_MAP = {
  meituan: 'meituan',
  jd: 'jd',
  eleme: 'taobao',
  comment: 'meituan'
}

export default {
  name: 'DataMonitor',
  data () {
    return {
      // 月份选择器值，格式 "yyyy-MM"，默认当前月
      selectedMonth: (() => {
        const now = new Date()
        const m = now.getMonth() + 1
        return `${now.getFullYear()}-${m < 10 ? '0' + m : m}`
      })(),
      // 4 个日历各自独立的数据
      calendarData: {
        meituan: { loading: false, days: {} },
        jd: { loading: false, days: {} },
        eleme: { loading: false, days: {} },
        comment: { loading: false, days: {} }
      },
      // 数据源配置
      sourceList: [
        { key: 'meituan', label: '美团运营数据' },
        { key: 'jd', label: '京东运营数据' },
        { key: 'eleme', label: '闪购运营数据' },
        { key: 'comment', label: '美团评论' }
      ]
    }
  },
  computed: {
    // 月份选择器的值（yyyy-MM 字符串）
    currentMonth: {
      get () {
        return this.selectedMonth
      },
      set (val) {
        this.selectedMonth = val
      }
    },
    // el-calendar 的 v-model 需要一个 Date 对象
    calendarDate: {
      get () {
        const [y, m] = this.currentMonth.split('-')
        return new Date(parseInt(y), parseInt(m) - 1, 1)
      },
      set () {
        // 留空，防止日历内部点击切换月份导致跳变
        // 月份只能通过顶部的 el-date-picker 切换
      }
    },
    // 是否有任意日历在加载
    anyLoading () {
      return Object.values(this.calendarData).some(item => item.loading)
    }
  },
  created () {
    // 请求令牌，防止快速切换月份时旧响应覆盖新数据
    this._loadTokens = {}
    this.loadAll()
  },
  methods: {
    // 获取年和月
    getYearAndMonth () {
      const [year, month] = this.currentMonth.split('-')
      return { year, month }
    },
    // 加载单个数据源
    async loadCalendar (source) {
      // 递增令牌，丢弃过期响应
      const token = (this._loadTokens[source] || 0) + 1
      this._loadTokens[source] = token
      this.calendarData[source].loading = true
      try {
        const { year, month } = this.getYearAndMonth()
        const { data: res } = await this.$http.get('yunwei/dataMonitor/calendar', {
          params: { year, month, source }
        })
        // 若期间又发起了新请求，则丢弃当前过期响应
        if (token !== this._loadTokens[source]) return
        if (res.meta.status !== 200) {
          this.$message.error(res.meta.msg)
          return
        }
        this.calendarData[source].days = res.data.days || {}
      } catch (err) {
        if (token !== this._loadTokens[source]) return
        this.$message.error('加载数据失败')
      } finally {
        if (token === this._loadTokens[source]) {
          this.calendarData[source].loading = false
        }
      }
    },
    // 全部加载
    loadAll () {
      this.sourceList.forEach(item => {
        this.loadCalendar(item.key)
      })
    },
    // 单独刷新某个日历
    refreshCalendar (source) {
      this.loadCalendar(source)
    },
    // 获取某天某数据源的条数
    getCount (source, data) {
      return this.calendarData[source].days[data.day] || 0
    },
    // 从 "2026-06-15" 提取日号
    getDayNumber (day) {
      return parseInt(day.split('-')[2])
    },
    // 上月/下月快捷切换
    changeMonth (delta) {
      const [y, m] = this.selectedMonth.split('-').map(Number)
      const d = new Date(y, m - 1 + delta, 1)
      const yy = d.getFullYear()
      const mm = d.getMonth() + 1
      this.selectedMonth = `${yy}-${mm < 10 ? '0' + mm : mm}`
      this.loadAll()
    },
    // 点击未采集日期，跳转到采集页面
    handleDayClick (source, data) {
      if (this.getCount(source, data) > 0) return
      const platform = SOURCE_PLATFORM_MAP[source] || 'meituan'
      this.$router.push({
        path: '/yunwei/waimai',
        query: { platform, date: data.day }
      })
    }
  }
}
</script>

<style scoped>
.toolbar-label {
  font-size: 14px;
  color: #606266;
  margin-right: 4px;
}

.calendar-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-top: 16px;
}

.calendar-item {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
}

.calendar-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f5f7fa;
  font-weight: bold;
  font-size: 14px;
  color: #303133;
}

/* 隐藏 el-calendar 自带的标题栏 */
.monitor-calendar >>> .el-calendar__header {
  display: none;
}

/* 缩小日历单元格高度 */
.monitor-calendar >>> .el-calendar-table .el-calendar-day {
  min-height: 46px;
  height: 46px;
  padding: 2px;
}

/* 隐藏日历表头中的"今"按钮 */
.monitor-calendar >>> .el-calendar__body {
  padding: 0 8px 8px;
}

/* 日期单元格内容 */
.calendar-day {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.day-number {
  font-size: 13px;
  line-height: 1;
}

/* 有数据的日期 - 绿色背景 */
.calendar-day.has-data {
  background: #e8f5e9;
  color: #67c23a;
}

.has-data .day-number {
  font-weight: bold;
  color: #67c23a;
}

.cell-count {
  font-size: 11px;
  color: #67c23a;
  font-weight: bold;
  line-height: 1;
  margin-top: 2px;
}

/* 相邻月日期淡化 */
.other-month {
  opacity: 0.3;
}

/* 未采集日期 - 可点击 */
.calendar-day.no-data {
  cursor: pointer;
  color: #c0c4cc;
}

.calendar-day.no-data:hover {
  background: #f0f7ff;
  color: #409eff;
}

.cell-hint {
  font-size: 10px;
  color: #c0c4cc;
  line-height: 1;
  margin-top: 2px;
}

.calendar-day.no-data:hover .cell-hint {
  color: #409eff;
}
</style>
